// ---------------------------------------------------------------------------
// LiveMonitoringPanel — corpo riusabile del live monitoring (SP-5f).
//
// Estratto da LiveMonitoringPage così che lo stesso contenuto possa essere
// montato sia nella route dedicata `/live` sia dentro la Dashboard (Home).
// NON include il PageHeading: è chrome di pagina, lo aggiunge il consumer.
// ---------------------------------------------------------------------------

import { useState, useEffect } from 'react';
import { useTenantContext } from '../../auth/useTenantContext';
import { useRole } from '../../auth/useRole';
import { useTenantAdminWarehouses } from '../../api/adminWarehouses';
import { useLiveStream } from '../../hooks/useLiveStream';
import { useLiveOperators } from '../../api/live';
import { OperatorView } from './OperatorView';
import { VpSelect } from '../ui/VpSelect';
import { DisconnectBanner } from './DisconnectBanner';

// SP-8h — il live monitoring mostra SOLO la vista "Operatori": le viste
// "Per pick-list" (TaskView) e "Matrix" (MatrixView) + il selettore
// (ViewSwitcher) sono stati rimossi.
const WH_STORAGE_KEY = 'vp.live_warehouse_id';

export function LiveMonitoringPanel() {
  const { token, tenantCode, warehouseId: ctxWarehouseId } = useTenantContext();
  const { isPlatformAdmin, isTenantAdmin } = useRole();

  // Tenant-admin / platform-admin non hanno warehouse legato alla membership.
  // Carico la lista warehouses del tenant e mostro un picker in-page.
  const needsPicker = ctxWarehouseId === null && (isPlatformAdmin || isTenantAdmin);
  const warehousesQuery = useTenantAdminWarehouses(needsPicker ? token : null, needsPicker ? tenantCode : null);

  // Selezione locale: parte dall'header context, sennò da localStorage, sennò null.
  const [selectedWh, setSelectedWh] = useState<number | null>(() => {
    if (ctxWarehouseId !== null) return ctxWarehouseId;
    try {
      const stored = localStorage.getItem(WH_STORAGE_KEY);
      if (stored !== null && stored !== '') {
        const n = Number(stored);
        if (Number.isFinite(n) && n > 0) return n;
      }
    } catch { /* ignore */ }
    return null;
  });

  // Se l'header espone un warehouseId, ha priorità sul picker locale.
  useEffect(() => {
    if (ctxWarehouseId !== null) setSelectedWh(ctxWarehouseId);
  }, [ctxWarehouseId]);

  const warehouseId = ctxWarehouseId ?? selectedWh;

  useEffect(() => {
    if (selectedWh !== null && ctxWarehouseId === null) {
      try { localStorage.setItem(WH_STORAGE_KEY, String(selectedWh)); } catch { /* ignore */ }
    }
  }, [selectedWh, ctxWarehouseId]);

  const { state, status, reconnect } = useLiveStream({
    token,
    tenantCode,
    warehouseId,
    enabled: warehouseId !== null && token !== null,
  });

  // Roster completo operatori del magazzino (anche non collegati) per la vista
  // operatori: gli offline vengono mostrati incrociando il roster con le sessioni.
  const operatorsRoster = useLiveOperators(token, tenantCode, warehouseId).data ?? [];

  if (warehouseId === null) {
    return needsPicker ? (
      <div>
        <p style={{ color: 'var(--vp-text-2)', marginTop: 0 }}>
          Seleziona un magazzino per iniziare il live monitoring.
        </p>
        {warehousesQuery.isLoading ? (
          <p>Caricamento magazzini…</p>
        ) : warehousesQuery.data && warehousesQuery.data.data.length > 0 ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 400 }}>
            Magazzino
            <VpSelect
              ariaLabel="Magazzino" placeholder="— scegli —"
              options={warehousesQuery.data.data.map((w) => ({ value: w.id, label: `${w.name} (${w.code})` }))}
              value={null}
              onChange={(v) => { const n = Number(v); if (Number.isFinite(n) && n > 0) setSelectedWh(n); }}
            />
          </label>
        ) : (
          <p style={{ color: 'var(--vp-text-2)' }}>Nessun magazzino configurato in questo tenant.</p>
        )}
      </div>
    ) : (
      <p style={{ color: 'var(--vp-text-2)' }}>
        Seleziona un magazzino dall&apos;header per iniziare.
      </p>
    );
  }

  const sessions = Array.from(state.sessions.values());

  return (
    <div>
      {needsPicker && warehousesQuery.data && (
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 'var(--vp-text-sm)', color: 'var(--vp-text-2)' }}>
            Magazzino:
          </label>
          <div style={{ minWidth: 240 }}>
            <VpSelect
              ariaLabel="Magazzino"
              options={warehousesQuery.data.data.map((w) => ({ value: w.id, label: `${w.name} (${w.code})` }))}
              value={warehouseId}
              onChange={(v) => setSelectedWh(Number(v))}
            />
          </div>
        </div>
      )}
      {(status === 'disconnected' || status === 'reconnecting') && (
        <DisconnectBanner status={status} onRetry={reconnect} />
      )}
      <OperatorView sessions={sessions} roster={operatorsRoster} />
    </div>
  );
}
